import { toggleArray } from '@domain-group/javascript-utils'
import { useSetState } from '@domain-group/react-hooks'

import useInvoice from '@hooks/useInvoice'

import type { CampaignModel, CampaignItemModel, OrderModel } from '@models/types'

type DefaultState = {
  selectedCampaignItemIds: number[],
  selectedOrderIds: number[],
}

type SetState = (state: Partial<DefaultState>) => void

const itemKeys = {
  CampaignItem: 'selectedCampaignItemIds',
  Order: 'selectedOrderIds',
} as const

type SelectItemParams = {
  item: CampaignItemModel | OrderModel,
  setState: SetState,
  state: DefaultState,
}

const selectItem = (params: SelectItemParams) => {
  const { item, setState, state } = params

  // @ts-expect-error TS7053
  const itemKey = itemKeys[item.type]

  setState({
    // @ts-expect-error TS7053
    [itemKey]: toggleArray(state[itemKey], item.id),
  })
}

type ToggleAllParams = {
  campaignItems: CampaignItemModel[],
  orders: OrderModel[],
  setState: SetState,
  state: DefaultState,
}

const toggleAll = (params: ToggleAllParams) => {
  const {
    campaignItems, orders, setState, state,
  } = params

  const { selectedCampaignItemIds, selectedOrderIds } = state

  const totalItemCount = orders.length + campaignItems.length
  const totalSelectedCount = selectedCampaignItemIds.length + selectedOrderIds.length

  if (totalItemCount === totalSelectedCount){
    setState({
      selectedCampaignItemIds: [],
      selectedOrderIds: [],
    })

    return
  }

  setState({
    selectedCampaignItemIds: campaignItems.map(item => item.id),
    selectedOrderIds: orders.map(order => order.id),
  })
}

const defaultState: DefaultState = {
  selectedCampaignItemIds: [],
  selectedOrderIds: [],
}

type UseApprovedItemSelectorOptions = {
  campaign: CampaignModel,
  campaignItems: CampaignItemModel[],
  orders: OrderModel[],
}

function useApprovedItemSelector(options: UseApprovedItemSelectorOptions) {
  const { campaign, campaignItems, orders } = options

  const [state, setState] = useSetState(defaultState)
  const { selectedCampaignItemIds, selectedOrderIds } = state

  const hasSelectedItems = !!selectedCampaignItemIds.length || !!selectedOrderIds.length

  const invoicePayload = useInvoice({
    creditor_id: campaign.agency_id,
    creditor_type: 'Agency',
    campaign_id: campaign.id,
  })

  const {
    callbacks: {
      createInvoiceFromItems,
    },
    creating: creatingInvoice,
  } = invoicePayload

  return {
    callbacks: {
      createInvoiceFromItems: () => createInvoiceFromItems({
        campaignItemIds: selectedCampaignItemIds,
        orderIds: selectedOrderIds,
      }),
      selectItem: (item: CampaignItemModel | OrderModel) => selectItem({
        item,
        setState,
        state,
      }),
      toggleAll: () => toggleAll({
        campaignItems,
        orders,
        setState,
        state,
      }),
    },
    creatingInvoice,
    hasSelectedItems,
    selectedCampaignItemIds,
    selectedOrderIds,
  }
}

export type ApprovedItemSelectorPayload = ReturnType<typeof useApprovedItemSelector>

export default useApprovedItemSelector
